import { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import startCase from 'lodash/startCase';
import upperCase from 'lodash/upperCase';
import type { ImportEventTableColumnConfig } from '../../../../config/importEventFromPlanConfig';
import { tpoTableCellTestId, tpoTableColTestId } from '../../../../config/testIds';
import { resolveFieldLabel, type FieldValueLabelMaps } from '../../../../utils/resolveFieldLabel';
import type { ImportableEventRow } from '../queries/useImportableEvents';

const TABLE_SURFACE = 'import_events';

/** Traverses a dot-notation path on a row object and returns the string value. */
const getNestedValue = (row: ImportableEventRow, dotPath: string): string => {
    const parts = dotPath.split('.');
    let val: unknown = row;
    for (const part of parts) {
        if (val == null || typeof val !== 'object') return '';
        val = (val as Record<string, unknown>)[part];
    }
    return String(val ?? '');
};

const buildColumns = (
    cols: ImportEventTableColumnConfig[],
    labelMaps: FieldValueLabelMaps,
): ColumnsType<ImportableEventRow> =>
    cols.map((col) => {
        const antdDataIndex: string | string[] = col.dataIndex.includes('.') ? col.dataIndex.split('.') : col.dataIndex;

        let render: ((val: unknown) => string) | undefined;
        if (col.renderAs === 'startCase') {
            render = (val: unknown) => startCase(String(val ?? '')) || '-';
        } else if (col.renderAs === 'upperCase') {
            render = (val: unknown) => upperCase(String(val ?? '')) || '-';
        } else if (col.renderAs === 'date') {
            render = (val: unknown) => (val ? dayjs(String(val)).format('MM/DD/YYYY') : '-');
        } else if (col.renderAs === 'configLabel') {
            render = (val: unknown) => resolveFieldLabel(String(val ?? ''), labelMaps, col.resolveLabelFrom) || '-';
        }

        const sorter =
            col.sortBy === 'date'
                ? (a: ImportableEventRow, b: ImportableEventRow) =>
                      dayjs(getNestedValue(a, col.dataIndex)).unix() - dayjs(getNestedValue(b, col.dataIndex)).unix()
                : (a: ImportableEventRow, b: ImportableEventRow) =>
                      getNestedValue(a, col.dataIndex).localeCompare(getNestedValue(b, col.dataIndex));

        return {
            key: col.key,
            title: col.title,
            dataIndex: antdDataIndex,
            ...(col.width !== undefined && { width: col.width }),
            ellipsis: col.ellipsis ?? true,
            ...(render !== undefined && { render }),
            sorter,
            sortDirections: (col.sortDirections ?? ['descend', 'ascend']) as SortOrder[],
            onHeaderCell: () =>
                ({
                    'data-testid': tpoTableColTestId(TABLE_SURFACE, col.key),
                } as React.ThHTMLAttributes<HTMLTableCellElement>),
            onCell: (record: ImportableEventRow) =>
                ({
                    'data-testid': tpoTableCellTestId(TABLE_SURFACE, record.promo_id, col.key),
                } as React.TdHTMLAttributes<HTMLTableCellElement>),
        };
    });

/** Builds the Import-Events table columns from the tenant's column config. */
export function useImportEventColumns(
    columnConfigs: ImportEventTableColumnConfig[],
    labelMaps: FieldValueLabelMaps,
): ColumnsType<ImportableEventRow> {
    return useMemo(() => buildColumns(columnConfigs, labelMaps), [columnConfigs, labelMaps]);
}
